import React, { useState, useEffect } from 'react';
import { Select, Button, Space } from 'antd';
import { DollarOutlined, FileAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { accountingService } from '../../services/accountingService';
import { patientService } from '../../services';
import './Accounting.css';

function Accounting() {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [dashboard, setDashboard] = useState(null);
    const [payments, setPayments] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [patients, setPatients] = useState([]);
    const [period, setPeriod] = useState('today');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);

    const [paymentForm, setPaymentForm] = useState({
        patientId: '',
        amount: '',
        paymentMethod: 'cash',
        notes: ''
    });

    const [expenseForm, setExpenseForm] = useState({
        category: 'supplies',
        description: '',
        amount: '',
        expenseDate: new Date().toISOString().split('T')[0],
        vendorName: ''
    });

    useEffect(() => {
        loadPatients();
    }, []);

    useEffect(() => {
        if (activeTab === 'dashboard') {
            loadDashboard();
        } else if (activeTab === 'payments') {
            loadPayments();
        } else if (activeTab === 'expenses') {
            loadExpenses();
        }
    }, [activeTab, period]);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    const loadDashboard = async () => {
        try {
            const data = await accountingService.getDashboard(period);
            setDashboard(data);
        } catch (error) {
            console.error('Error loading dashboard:', error);
        }
    };

    const loadPayments = async () => {
        try {
            const data = await accountingService.getPayments();
            setPayments(data);
        } catch (error) {
            console.error('Error loading payments:', error);
        }
    };

    const loadExpenses = async () => {
        try {
            const data = await accountingService.getExpenses();
            setExpenses(data);
        } catch (error) {
            console.error('Error loading expenses:', error);
        }
    };

    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        try {
            await accountingService.recordPayment(paymentForm);
            setShowPaymentModal(false);
            setPaymentForm({ patientId: '', amount: '', paymentMethod: 'cash', notes: '' });
            loadPayments();
            loadDashboard();
        } catch (error) {
            alert('Error recording payment: ' + error.message);
        }
    };

    const handleExpenseSubmit = async (e) => {
        e.preventDefault();
        try {
            await accountingService.recordExpense(expenseForm);
            setShowExpenseModal(false);
            setExpenseForm({
                category: 'supplies',
                description: '',
                amount: '',
                expenseDate: new Date().toISOString().split('T')[0],
                vendorName: ''
            });
            loadExpenses();
            loadDashboard();
        } catch (error) {
            alert('Error recording expense: ' + error.message);
        }
    };

    return (
        <div className="accounting-container">
            <div className="accounting-header">
                <h1>{t('accountingManagement')}</h1>
                <div className="header-actions">
                    <Space>
                        <Button
                            type="primary"
                            icon={<DollarOutlined />}
                            onClick={() => setShowPaymentModal(true)}
                            size="large"
                        >
                            {t('recordPayment')}
                        </Button>
                        <Button
                            icon={<FileAddOutlined />}
                            onClick={() => setShowExpenseModal(true)}
                            size="large"
                        >
                            {t('addExpense')}
                        </Button>
                    </Space>
                </div>
            </div>

            <div className="tabs">
                <button
                    className={activeTab === 'dashboard' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('dashboard')}
                >
                    {t('dashboard')}
                </button>
                <button
                    className={activeTab === 'payments' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('payments')}
                >
                    {t('payments')}
                </button>
                <button
                    className={activeTab === 'expenses' ? 'tab active' : 'tab'}
                    onClick={() => setActiveTab('expenses')}
                >
                    {t('expenses')}
                </button>
            </div>

            {activeTab === 'dashboard' && (
                <div className="dashboard-content">
                    <div className="period-selector">
                        <button className={period === 'today' ? 'active' : ''} onClick={() => setPeriod('today')}>{t('today')}</button>
                        <button className={period === 'week' ? 'active' : ''} onClick={() => setPeriod('week')}>{t('week')}</button>
                        <button className={period === 'month' ? 'active' : ''} onClick={() => setPeriod('month')}>{t('month')}</button>
                    </div>

                    <div className="financial-metrics">
                        <div className="metric-card revenue">
                            <div className="metric-icon">💰</div>
                            <div className="metric-value">{dashboard?.totalRevenue?.toLocaleString() || 0} IQD</div>
                            <div className="metric-label">{t('totalRevenue')}</div>
                        </div>
                        <div className="metric-card expenses">
                            <div className="metric-icon">💸</div>
                            <div className="metric-value">{dashboard?.totalExpenses?.toLocaleString() || 0} IQD</div>
                            <div className="metric-label">{t('totalExpenses')}</div>
                        </div>
                        <div className="metric-card profit">
                            <div className="metric-icon">📈</div>
                            <div className="metric-value">{dashboard?.netProfit?.toLocaleString() || 0} IQD</div>
                            <div className="metric-label">{t('netProfit')}</div>
                        </div>
                        <div className="metric-card patients">
                            <div className="metric-icon">👥</div>
                            <div className="metric-value">{dashboard?.patientCount || 0}</div>
                            <div className="metric-label">{t('patients')}</div>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'payments' && (
                <div className="table-content">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('date')}</th>
                                <th>{t('patient')}</th>
                                <th>{t('amount')}</th>
                                <th>{t('method')}</th>
                                <th>{t('receiptNumber')}</th>
                                <th>{t('notes')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {payments.map((payment) => (
                                <tr key={payment._id}>
                                    <td>{new Date(payment.paymentDate).toLocaleDateString()}</td>
                                    <td>{payment.patient?.name || 'N/A'}</td>
                                    <td className="amount">{payment.amount.toLocaleString()} IQD</td>
                                    <td>
                                        <span className={`badge ${payment.paymentMethod}`}>
                                            {payment.paymentMethod}
                                        </span>
                                    </td>
                                    <td>{payment.receiptNumber}</td>
                                    <td>{payment.notes || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {activeTab === 'expenses' && (
                <div className="table-content">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>{t('date')}</th>
                                <th>{t('category')}</th>
                                <th>{t('description')}</th>
                                <th>{t('amount')}</th>
                                <th>{t('vendor')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map((expense) => (
                                <tr key={expense._id}>
                                    <td>{new Date(expense.expenseDate).toLocaleDateString()}</td>
                                    <td>
                                        <span className={`badge ${expense.category}`}>
                                            {expense.category}
                                        </span>
                                    </td>
                                    <td>{expense.description}</td>
                                    <td className="amount">{expense.amount.toLocaleString()} IQD</td>
                                    <td>{expense.vendorName || '-'}</td>
                                    <td>
                                        <button
                                            className="btn-delete"
                                            onClick={() => {
                                                if (window.confirm('Delete this expense?')) {
                                                    accountingService.deleteExpense(expense._id).then(() => {
                                                        loadExpenses();
                                                        loadDashboard();
                                                    });
                                                }
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('recordPayment')}</h2>
                        <form onSubmit={handlePaymentSubmit}>
                            <div className="form-group">
                                <label>{t('patient')}</label>
                                <Select
                                    showSearch
                                    value={paymentForm.patientId || undefined}
                                    onChange={(value) => setPaymentForm({ ...paymentForm, patientId: value })}
                                    placeholder={t('searchAndSelectPatient')}
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    required
                                >
                                    {patients.map((patient) => (
                                        <Select.Option
                                            key={patient._id}
                                            value={patient._id}
                                            label={`${patient.name} - ${patient.phoneNumber}`}
                                        >
                                            {patient.name} - {patient.phoneNumber}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="form-group">
                                <label>{t('amount')} (IQD)</label>
                                <input
                                    type="number"
                                    value={paymentForm.amount}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                                    required
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('paymentMethod')}</label>
                                <select
                                    value={paymentForm.paymentMethod}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                                >
                                    <option value="cash">{t('cash')}</option>
                                    <option value="card">{t('card')}</option>
                                    <option value="insurance">{t('insurance')}</option>
                                    <option value="installment">{t('installment')}</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('notes')}</label>
                                <textarea
                                    value={paymentForm.notes}
                                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                                    rows="3"
                                />
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => setShowPaymentModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('recordPayment')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Expense Modal */}
            {showExpenseModal && (
                <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('addExpense')}</h2>
                        <form onSubmit={handleExpenseSubmit}>
                            <div className="form-group">
                                <label>{t('category')}</label>
                                <select
                                    value={expenseForm.category}
                                    onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
                                >
                                    <option value="supplies">{t('supplies')}</option>
                                    <option value="equipment">{t('equipment')}</option>
                                    <option value="salaries">{t('salaries')}</option>
                                    <option value="utilities">{t('utilities')}</option>
                                    <option value="other">{t('other')}</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('description')}</label>
                                <input
                                    type="text"
                                    value={expenseForm.description}
                                    onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('amount')} (IQD)</label>
                                <input
                                    type="number"
                                    value={expenseForm.amount}
                                    onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                                    required
                                    min="0"
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('date')}</label>
                                <input
                                    type="date"
                                    value={expenseForm.expenseDate}
                                    onChange={(e) => setExpenseForm({ ...expenseForm, expenseDate: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>{t('vendorName')}</label>
                                <input
                                    type="text"
                                    value={expenseForm.vendorName}
                                    onChange={(e) => setExpenseForm({ ...expenseForm, vendorName: e.target.value })}
                                />
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => setShowExpenseModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('addExpense')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Accounting;
